import { useEffect, useState } from 'react';
import { useMap, ImageOverlay } from 'react-leaflet';
import { useLayers } from '../../context/LayersContext';
import { flattenLeaves, UnifiedLayerConfig } from '../../utils/index';
import { mockLayers } from '../../global/constants/layers';
import L from 'leaflet';

const PE_SW = L.latLng(-9.5, -41.5);
const PE_NE = L.latLng(-7.0, -34.8);

function getBoundsPixelSize(
  map: L.Map,
  bounds: [[number, number], [number, number]]
): [number, number] {
  const sw = map.latLngToContainerPoint(L.latLng(bounds[0][0], bounds[0][1]));
  const ne = map.latLngToContainerPoint(L.latLng(bounds[1][0], bounds[1][1]));
  const width = Math.round(Math.abs(ne.x - sw.x));
  const height = Math.round(Math.abs(sw.y - ne.y));

  const max = 1024;
  const ratio = Math.min(1, max / Math.max(width, height));
  return [Math.round(width * ratio), Math.round(height * ratio)];
}

export function RenderAllLayers() {
  const map = useMap();
  const { layers } = useLayers();
  const [layerUrls, setLayerUrls] = useState<
    {
      name: string;
      url: string;
      bounds: [[number, number], [number, number]];
    }[]
  >([]);

  const allConfigs: UnifiedLayerConfig[] = flattenLeaves(mockLayers);

  const updateLayerImages = () => {
    const view = map.getBounds();
    const vSW = view.getSouthWest();
    const vNE = view.getNorthEast();

    const iSWLat = Math.max(vSW.lat, PE_SW.lat);
    const iSWLng = Math.max(vSW.lng, PE_SW.lng);
    const iNELat = Math.min(vNE.lat, PE_NE.lat);
    const iNELng = Math.min(vNE.lng, PE_NE.lng);
    if (iSWLat > iNELat || iSWLng > iNELng) {
      setLayerUrls([]);
      return;
    }

    const dynamicBounds: [[number, number], [number, number]] = [
      [iSWLat, iSWLng],
      [iNELat, iNELng],
    ];

    const dynamicBoundsPNG32: [[number, number], [number, number]] = [
      [vSW.lat, vSW.lng],
      [vNE.lat, vNE.lng],
    ];
    const bboxPNG32 = [vSW.lng, vSW.lat, vNE.lng, vNE.lat].join(',');
    const sizePNG32 = getBoundsPixelSize(map, dynamicBoundsPNG32).join(',');

    const bboxDefault = [iSWLng, iSWLat, iNELng, iNELat].join(',');
    const sizeDefault = getBoundsPixelSize(map, dynamicBounds).join(',');

    const urls = allConfigs
      .filter((cfg) => layers.includes(cfg.name))
      .map((cfg) => {
        const base =
          'https://gisepeprd2.epe.gov.br/arcgis/rest/services/SMA/WMS_Webmap_EPE/MapServer/export';

        if (cfg.png32) {
          return {
            name: cfg.name,
            url: `${base}?bbox=${bboxPNG32}&bboxSR=4326&imageSR=4326&format=png32&transparent=true&f=image&layers=show:${
              cfg.id
            }&size=${sizePNG32}&_ts=${Date.now()}`,

            bounds: dynamicBoundsPNG32,
          };
        } else {
          return {
            name: cfg.name,
            url: `${base}?bbox=${bboxDefault}&bboxSR=4326&imageSR=4326&format=png&transparent=true&f=image&layers=show:${cfg.id}&size=${sizeDefault}`,
            bounds: dynamicBounds,
          };
        }
      });

    setLayerUrls(urls);
  };

  useEffect(() => {
    updateLayerImages();
    map.on('zoomend', updateLayerImages);
    map.on('moveend', updateLayerImages);
    return () => {
      map.off('zoomend', updateLayerImages);
      map.off('moveend', updateLayerImages);
    };
  }, [layers, map]);

  return (
    <>
      {layerUrls.map((l) => (
        <ImageOverlay key={l.name} url={l.url} bounds={l.bounds} />
      ))}
    </>
  );
}
